<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import CollectionForm from './CollectionForm.vue';
import useMyCollections from '../use-my-collections';
import type { CollectionFormPatch, CollectionPatch, ContentReference, NewContentCollection } from '../types';
import type { ContentCollection } from '@ilteducation/content-collections-types';
import { useDisplay } from 'vuetify';
import { useConfigHelper } from '@ilteducation/config';
import { DrawerBottom } from '@ilteducation/components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import useLocale from '../composables/useLocale';

export type Props = {
  contentReferences?: ContentReference[];
  collection?: ContentCollection;
  allAvailableIconGroups?: Record<string, string[]>;
  iconGroups?: string[];
  selectedIcon?: string;
  showIcons?: boolean;
  context: string;
  isOnline?: boolean;
  isPublic?: boolean;
  rounded?: 'sm' | 'default' | 'lg' | 'xl';
};

const props = withDefaults(defineProps<Props>(), {
  showIcons: true,
  isOnline: true,
  rounded: 'lg',
  isPublic: false,
});

const emit = defineEmits<{
  (e: 'create-collection', id: string, name: string): void;
  (e: 'update-collection', id: string): void;
  (e: 'close'): void;
}>();

const { create, update } = useMyCollections(props.context, 500);
const { t } = useLocale();
const showDialog = ref(true);
const { collection, contentReferences } = toRefs(props);
const { xs } = useDisplay();
const isMobileDevice = computed(() => {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  return (
    /android|iphone|ipod|blackberry|bb|playbook|windows phone|webos|opera mini|opera mobi|mobile/i.test(ua) &&
    !/ipad|tablet/i.test(ua)
  );
});

const title = computed(() =>
  collection.value?.id ? t('content_collections.label_edit_collection') : t('content_collections.label_new_collection'),
);

const realm = useConfigHelper().getRealm();

const handleSave = async (formPatch: CollectionFormPatch) => {
  if (collection?.value) {
    const patch: CollectionPatch = { ...formPatch };

    if (collection.value.contents.type === 'ContentsByReferences' && contentReferences.value?.length) {
      const { contents } = collection.value;

      patch.contents = {
        type: 'ContentsByReferences',
        references: [...new Set([...contents.references, ...(contentReferences.value ?? [])])],
      };
    }

    await update(collection.value.id, patch).then(() => {
      emit('update-collection', collection.value!.id);
    });
  } else {
    await create({
      ...formPatch,
      context: props.context,
      contents: {
        type: 'ContentsByReferences',
        references: contentReferences.value ?? [],
      },
      isPublic: props.isPublic,
      realm: realm,
    } as NewContentCollection).then((newCollection) => {
      emit('create-collection', newCollection.id, newCollection.name);
    });
  }
  handleClose();
};

const handleClose = () => {
  emit('close');
};

const buttonRadius: Record<string, string> = {
  sm: 'xs',
  default: 'sm',
  lg: 'true',
  xl: 'lg',
};
</script>

<template>
  <DrawerBottom
    v-if="xs || isMobileDevice"
    :modelValue="showDialog"
    color="surface-container-lowest"
    :title="title"
    @close="handleClose"
  >
    <CollectionForm
      @save="handleSave"
      @close="handleClose"
      :context="context"
      :all-available-icon-groups="allAvailableIconGroups"
      :icon-groups="iconGroups"
      :selected-icon="selectedIcon"
      :show-icons="showIcons"
      :collection="collection"
      :is-online="isOnline"
      :rounded="rounded"
    >
      <template #label_for_icon>
        <slot name="label_for_icon"></slot>
      </template>
    </CollectionForm>
  </DrawerBottom>
  <v-dialog
    v-else
    transition="dialog-bottom-transition"
    v-model="showDialog"
    :max-width="600"
    :max-height="700"
    scrollable
    @keydown.esc="handleClose"
    @click:outside="handleClose"
  >
    <v-card
      class="overflow-auto"
      :class="['edit-collection', 'overflow-auto', rounded !== 'default' ? `rounded-${rounded}` : 'rounded']"
      fluid
      flat
    >
      <v-card-title class="py-6 ma-0">
        <slot name="title">
          <h2 class="text-center ma-0">{{ title }}</h2>
        </slot>
        <v-btn
          variant="text"
          class="close-btn position-absolute"
          color="secondary"
          @click="handleClose"
          :rounded="buttonRadius[rounded]"
          :aria-label="t('label_close')"
        >
          <FontAwesomeIcon icon="fa-solid fa-xmark" size="lg" />
        </v-btn>
      </v-card-title>
      <CollectionForm
        @save="handleSave"
        @close="handleClose"
        :context="context"
        :all-available-icon-groups="allAvailableIconGroups"
        :icon-groups="iconGroups"
        :selected-icon="selectedIcon"
        :show-icons="showIcons"
        :collection="collection"
        :is-online="isOnline"
        :rounded="rounded"
      >
        <template #label_for_icon>
          <slot name="label_for_icon"></slot>
        </template>
      </CollectionForm>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.close-btn {
  right: 12px;
  top: 12px;
  z-index: 1;
  min-width: unset;
}
:deep(.v-card) {
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
</style>
